import { Button, type FormInstance, Select } from 'antd';
import isEqual from 'lodash/isEqual';
import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import DefaultForm from 'components/composites/DefaultForm';
import DefaultFormItem from 'components/composites/DefaultFormItem';
import LoadingSpinner from 'components/composites/LoadingSpinner';
import { FULL_ACCESS } from 'constants/internal-admin-roles';
import ActionTypes from 'store/modules/countries/constants';
import { useCountriesAsArray } from 'store/modules/countries/hooks';
import { makeSelectIsLoading } from 'store/modules/loading/selectors';
import { useMyUserInternalAdminCountryAccess } from 'store/modules/myUser/hooks';
import { InternalAdminCountryAccess } from 'store/modules/myUser/types';
import Country from 'types/models/country';
import Enterprise, { EnterpriseStatus, Industry } from 'types/models/enterprise';

interface Props {
  form?: FormInstance;
  initialValues?: Partial<Enterprise>;
}

const selectIsLoading = makeSelectIsLoading([
  ActionTypes.LOAD_COUNTRIES_REQUEST
]);

function BackOfficeEnterpriseFilterForm({
  form,
  initialValues
}: Props): JSX.Element {
  const isLoadingCountries = useSelector(selectIsLoading);

  let countries: Country[] = useCountriesAsArray();
  const countryAccess: InternalAdminCountryAccess = useMyUserInternalAdminCountryAccess();
  if (countryAccess !== FULL_ACCESS) {
    countries = countries.filter(country => countryAccess.includes(country.abbreviation));
  }
  const countryOptions = useMemo(
    () =>
      countries.map(({ name }) => ({
        label: name,
        value: name
      })),
    [countries]
  );

  return (
    <DefaultForm
      form={form}
      layout="vertical"
      name="backOfficeEnterprise"
      autoComplete="off"
      initialValues={initialValues}
      requiredMark="optional"
    >
      <DefaultFormItem name="industry">
        <Select
          mode="multiple"
          options={[
            { label: 'Agriculture', value: Industry.AGRICULTURE },
            { label: 'Mining', value: Industry.MINING }
          ]}
          placeholder="Industry"
        />
      </DefaultFormItem>
      <DefaultFormItem name="status">
        <Select
          mode="multiple"
          options={[
            { label: 'Active', value: EnterpriseStatus.ACTIVE },
            { label: 'Demo', value: EnterpriseStatus.DEMO },
            { label: 'Disabled', value: EnterpriseStatus.DISABLED },
            { label: 'Duplicate', value: EnterpriseStatus.DUPLICATE },
            { label: 'Past due', value: EnterpriseStatus.PAST_DUE },
            { label: 'Test', value: EnterpriseStatus.TEST },
            { label: 'Trial', value: EnterpriseStatus.TRIAL },
            { label: 'Trial expired', value: EnterpriseStatus.TRIAL_EXPIRED }
          ]}
          placeholder="Status"
        />
      </DefaultFormItem>
      <DefaultFormItem name="billingAddress.state.country.name">
        {isLoadingCountries ? (
          <LoadingSpinner />
        ) : (
          <Select options={countryOptions} placeholder="Country" showSearch />
        )}
      </DefaultFormItem>
      <Button htmlType="submit" style={{ display: 'none' }} />
    </DefaultForm>
  );
}

export default memo(BackOfficeEnterpriseFilterForm, isEqual);
